// AdminPL.jsx — Phase 1 Billing Infrastructure
// Admin-only platform P&L dashboard: revenue vs. AI costs, per-agent breakdown.

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DollarSign, TrendingUp, TrendingDown, BarChart3, Users,
  RefreshCw, Calendar, Zap, ArrowUpRight, ArrowDownRight,
  Target, Award, Activity,
} from 'lucide-react';
import { apiGet } from '../lib/api.js';

const SERVICE_LABELS = {
  openai_chat:      'OpenAI Chat',
  openai_whisper:   'OpenAI Whisper',
  openai_tts:       'OpenAI TTS',
  elevenlabs_tts:   'ElevenLabs TTS',
  elevenlabs_clone: 'ElevenLabs Clone',
  perplexity_sonar: 'Perplexity Sonar',
  twilio_voice:     'Twilio Voice',
  twilio_sms:       'Twilio SMS',
  sendgrid_email:   'SendGrid Email',
};

const FEATURE_LABELS = {
  roleplay:         'AI Roleplay',
  underwriting_bot: 'Underwriting Bot',
  ai_dialer:        'AI Dialer',
  lead_summary:     'Lead Summary',
  drip_writer:      'Drip Writer',
  civix_assistant:  'CIVIX Assistant',
  compliance:       'Compliance',
  other:            'Other',
};

const SERVICE_COLORS = [
  '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b',
  '#ef4444', '#ec4899', '#6366f1', '#14b8a6', '#f97316',
];

function fmt(v) {
  if (v == null || isNaN(v)) return '$0.00';
  if (v < 0.01 && v > 0) return '<$0.01';
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(2)}`;
}

function fmtFull(v) {
  if (v == null || isNaN(v)) return '$0.00';
  return `$${v.toFixed(4)}`;
}

// Get months for selector
function getMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    opts.push({ val, label });
  }
  return opts;
}

export default function AdminPL() {
  const months = getMonthOptions();
  const [selectedMonth, setSelectedMonth] = useState(months[0].val);

  const { data: rawPnl, isLoading, refetch } = useQuery({
    queryKey: ['admin-pnl', selectedMonth],
    queryFn: () => apiGet('/ai-usage/admin/pnl', { month: selectedMonth }),
    staleTime: 60000,
  });

  const { data: rawAgents, isLoading: agentsLoading } = useQuery({
    queryKey: ['admin-agents-usage'],
    queryFn: () => apiGet('/ai-usage/admin/agents'),
    staleTime: 60000,
  });

  const pnl = rawPnl?.data || rawPnl || {};
  const agents = rawAgents?.data || rawAgents || [];

  const totalCost = pnl.totalCost || 0;
  const projected = pnl.projectedMonthly || 0;
  const runRate = pnl.runRatePerDay || 0;
  const costsByService = pnl.costsByService || [];
  const costsByFeature = pnl.costsByFeature || [];
  const agentBreakdown = pnl.agentBreakdown || [];
  const dailyTrend = pnl.dailyTrend || [];

  const maxServiceCost = Math.max(...costsByService.map((s) => s.totalCost), 0.001);
  const maxAgentCost = Math.max(...agentBreakdown.map((a) => a.totalCost), 0.001);

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-civix-purpleSoft/80 uppercase tracking-[0.3em] text-xs">Admin</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-civix-purpleSoft" />
            Platform P&amp;L
          </h1>
          <p className="text-white/50 text-sm mt-1">Real-time cost tracking across all agents and services.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="bg-civix-panelLight border border-civix-border/50 rounded-lg px-3 py-2 text-sm text-white focus:outline-hidden focus:border-civix-purple/50"
          >
            {months.map((m) => (
              <option key={m.val} value={m.val}>{m.label}</option>
            ))}
          </select>
          <button onClick={() => refetch()} className="btn-ghost text-sm flex items-center gap-1.5">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </header>

      {isLoading ? (
        <div className="p-12 text-center text-white/40">
          <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin mx-auto mb-3" />
          Loading P&L data…
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              label="AI Costs (Month)"
              value={fmt(totalCost)}
              sub="Total platform spend"
              icon={DollarSign}
              trend="neutral"
            />
            <MetricCard
              label="Projected (Month)"
              value={fmt(projected)}
              sub="At current run rate"
              icon={Target}
              trend={projected > totalCost * 1.5 ? 'up' : 'neutral'}
            />
            <MetricCard
              label="Daily Run Rate"
              value={fmt(runRate)}
              sub="Average per day"
              icon={Activity}
              trend="neutral"
            />
            <MetricCard
              label="Active Agents"
              value={agentBreakdown.length}
              sub={`of ${agents.length} total`}
              icon={Users}
              trend="neutral"
              isCurrency={false}
            />
          </div>

          {/* Note about revenue */}
          <div className="panel p-4 border border-civix-purple/20 bg-civix-purple/5">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-civix-purpleSoft shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-white/90">Pure Cost Passthrough Model</p>
                <p className="text-sm text-white/50 mt-0.5">
                  Agents pay exactly what we pay — no markup. Revenue comes from subscription fees.
                  The P&L shown here is the AI infrastructure cost side only.
                  {pnl.period && (
                    <span className="ml-1">
                      Period: {new Date(pnl.period.start).toLocaleDateString()} – {new Date(pnl.period.end).toLocaleDateString()}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Costs by Service + Feature */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="panel p-5">
              <h3 className="font-semibold mb-4">Cost by Service</h3>
              {costsByService.length === 0 ? (
                <p className="text-sm text-white/30">No data for this period</p>
              ) : (
                <div className="space-y-3">
                  {costsByService.map((row, i) => {
                    const pct = (row.totalCost / maxServiceCost) * 100;
                    return (
                      <div key={row.service}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-white/80">{SERVICE_LABELS[row.service] || row.service}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-white/30">{row.count?.toLocaleString()} calls</span>
                            <span className="text-sm font-mono font-medium">{fmt(row.totalCost)}</span>
                          </div>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${pct}%`, backgroundColor: SERVICE_COLORS[i % SERVICE_COLORS.length] }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="panel p-5">
              <h3 className="font-semibold mb-4">Cost by Feature</h3>
              {costsByFeature.length === 0 ? (
                <p className="text-sm text-white/30">No data for this period</p>
              ) : (
                <div className="space-y-3">
                  {costsByFeature.map((row, i) => {
                    const maxF = Math.max(...costsByFeature.map((r) => r.totalCost), 0.001);
                    const pct = (row.totalCost / maxF) * 100;
                    return (
                      <div key={row.feature}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-white/80">{FEATURE_LABELS[row.feature] || row.feature}</span>
                          <span className="text-sm font-mono font-medium">{fmt(row.totalCost)}</span>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${pct}%`, backgroundColor: SERVICE_COLORS[(i + 2) % SERVICE_COLORS.length] }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Daily Trend */}
          {dailyTrend.length > 0 && (
            <div className="panel p-5">
              <h3 className="font-semibold mb-4">Daily Cost Trend</h3>
              <DailyChart data={dailyTrend} />
            </div>
          )}

          {/* Per-Agent Breakdown */}
          <div className="panel overflow-hidden">
            <div className="p-4 border-b border-civix-border/50">
              <h3 className="font-semibold">Per-Agent Breakdown</h3>
            </div>
            {agentBreakdown.length === 0 ? (
              <div className="p-8 text-center text-white/30 text-sm">No agent usage data</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-civix-border/30">
                      <th className="text-left p-3 text-white/40 font-medium">Agent</th>
                      <th className="text-left p-3 text-white/40 font-medium">Email</th>
                      <th className="text-right p-3 text-white/40 font-medium">Events</th>
                      <th className="text-right p-3 text-white/40 font-medium">Month Spend</th>
                      <th className="text-right p-3 text-white/40 font-medium">Bar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-civix-border/20">
                    {agentBreakdown.map((row) => {
                      const pct = (row.totalCost / maxAgentCost) * 100;
                      return (
                        <tr key={String(row.agentId)} className="hover:bg-civix-panelLight/30 transition">
                          <td className="p-3 font-medium">{row.agentName}</td>
                          <td className="p-3 text-white/40 text-xs">{row.agentEmail}</td>
                          <td className="p-3 text-right text-white/60">{row.eventCount?.toLocaleString()}</td>
                          <td className="p-3 text-right font-mono text-green-400">{fmt(row.totalCost)}</td>
                          <td className="p-3 w-32">
                            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-civix-purple rounded-full"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-civix-border/50 bg-civix-panelLight/20">
                      <td className="p-3 font-semibold" colSpan={3}>Total</td>
                      <td className="p-3 text-right font-mono font-bold text-white">{fmt(totalCost)}</td>
                      <td className="p-3" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value, sub, icon: Icon, trend, isCurrency = true }) {
  return (
    <div className="panel p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-civix-purpleSoft/70" />
        <span className="text-xs text-white/50 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold font-mono">{value}</p>
      <p className="text-xs text-white/40 mt-1">{sub}</p>
    </div>
  );
}

function DailyChart({ data }) {
  const max = Math.max(...data.map((d) => d.totalCost), 0.001);
  const barW = Math.max(8, Math.min(40, Math.floor(800 / data.length) - 4));

  return (
    <div className="overflow-x-auto">
      <div className="flex items-end gap-1 h-32 min-w-max pb-6 relative">
        {data.map((d) => {
          const pct = (d.totalCost / max) * 100;
          const h = Math.max(2, (pct / 100) * 100);
          return (
            <div key={d._id} className="flex flex-col items-center group relative" style={{ width: barW }}>
              <div className="absolute bottom-6 hidden group-hover:block z-10 bg-civix-panelLight border border-civix-border/50 rounded-lg px-2 py-1 text-xs text-white whitespace-nowrap -translate-x-1/2 left-1/2">
                {d._id}: ${d.totalCost.toFixed(4)} ({d.count})
              </div>
              <div
                className="w-full bg-civix-purple/50 hover:bg-civix-purple/80 rounded-t transition-all"
                style={{ height: `${h}%` }}
              />
              <span className="text-white/30 text-xs mt-1 rotate-45 origin-left translate-y-2 translate-x-1">
                {d._id?.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
